import React from 'react';
import { FormikErrors } from 'formik';
import { MutationComponentOptions } from '@apollo/client/react/components';
import Promise from 'bluebird';
import classnames from 'classnames';
import Styled from 'styled';

import Collection from 'lib/collection/collection-view';
import ImageView from 'lib/images/image-view';

import { withUpload } from 'four-nets/ui-v2/form/upload-photofile/upload-photofile';
import Upload from 'four-nets/ui-v2/upload/upload';
import {
    UploadPhotofileMutation,
    UploadPhotofileMutationVariables,
} from 'four-nets/ui-v2/form/upload-photofile/mutations/upload-photofile.graphql';

import { Values } from '../create';
import defaultStyles from './short-message-image-upload.scss';

interface Props {
    styles: typeof defaultStyles;
    photofileUploads: any[];
    helpers: any;
    validateForm: (values?: any) => Promise<FormikErrors<Values>>;
    uploadFn: MutationComponentOptions<UploadPhotofileMutation, UploadPhotofileMutationVariables>;
    onCompleted: () => void;
}

class ShortMessageImageUpload extends React.PureComponent<Props> {
    promise?: Promise<any>;

    componentDidUpdate(prevProps: Props) {
        const { photofileUploads, uploadFn, validateForm } = this.props;

        if (photofileUploads !== prevProps.photofileUploads) {
            const chain = photofileUploads
                .filter(photofileUpload => !photofileUpload.photofile)
                .reduce(
                    (previous, item) => {
                        return previous.then(() => {
                            item.loading = true;
                            this.forceUpdate();

                            return uploadFn({ variables: { file: item.file } })
                                .then(({ data }: any) => {
                                    if (
                                        data.uploadPhotoFiles &&
                                        data.uploadPhotoFiles.photoFiles &&
                                        data.uploadPhotoFiles.photoFiles.length
                                    ) {
                                        item.setPhotoFile(data.uploadPhotoFiles.photoFiles[0]!);
                                        this.forceUpdate();
                                    }
                                    return data;
                                })
                                .catch(err => {
                                    item.error = err;
                                })
                                .finally(() => {
                                    item.loading = false;
                                });
                        });
                    },
                    this.promise && this.promise.isPending() ? this.promise : Promise.resolve()
                );

            this.promise = chain;
            this.promise
                .then(() => {
                    validateForm();
                })
                .finally(() => {
                    this.props.onCompleted();
                    this.promise = undefined;
                });
        }
    }

    componentWillUnmount() {
        if (this.promise && this.promise.isPending()) {
            this.promise.cancel();
        }
        this.promise = undefined;
    }

    render() {
        const { styles, photofileUploads, helpers, validateForm, uploadFn } = this.props;

        return (
            <Collection
                center={false}
                itemKey={(photofileUpload: any) => photofileUpload.key}
                items={photofileUploads}
                isDragabble={() => true}
                move={helpers.move}
                element={(photofileUpload: any) => (
                    <Upload
                        error={photofileUpload.photofile && !photofileUpload.photofile.id ? new Error() : null}
                        onRetry={() => uploadFn({ variables: { file: photofileUpload.file } })}
                        onClose={() => {
                            const index = photofileUploads.indexOf(photofileUpload);
                            if (index !== undefined) {
                                helpers.remove(index);
                                validateForm();
                            }
                        }}
                    >
                        <div className={classnames(styles.blurWrapper, { [styles.blur]: !photofileUpload.isValid() })}>
                            <ImageView
                                srcSet={photofileUpload.isValid() ? photofileUpload.photofile!.srcset! : ''}
                                src={photofileUpload.isValid() ? photofileUpload.photofile!.src! : photofileUpload.url}
                                width={90}
                                height={90}
                                sizes={{ xs: 90 }}
                                aspectFill={true}
                            />
                        </div>
                    </Upload>
                )}
                width={90}
                height={90}
            />
        );
    }
}

export default Styled(defaultStyles)(withUpload<Props>(ShortMessageImageUpload)) as any;
